// src/api/auth.ts · 前端认证 API（EP-010-S4 · 老板拍板：选 B 自写 UI，不用 Cognito Hosted UI）
//
// 提供 4 个方法：login() / register() / refresh() / logout()
//
// 设计依据：
//   - backend-proposal.md §v3-C.4 端点表（POST /auth/login · /auth/register · /auth/refresh）
//   - §v3-C.3 令牌载体：access token 走 JS 内存（Authorization 头）；refresh token 走
//     httpOnly cookie（JS 读不到，由浏览器自动随 credentials:'include' 带上）
//   - §v3-J 真相源原则：本文件**不读不写 localStorage**，登录态判定一律走 GET /auth/me 云端返回
//   - UX 文档 design/ux/08-auth-onboarding.md §2.3 / §7 F6：强制登录不可跳过（本文件只提供
//     "登录"这一条正向路径，**不提供任何"跳过登录"的旁路**）
//
// ★ 为什么单独成文件而不是塞进 client.ts：
//   client.ts 是"数据端点"（/health / /save），本文件是"身份端点"（/auth/*），
//   两者共用 requestInternal 同一条 fetch 链路（同 baseUrl / 同 ApiError / 同 credentials），
//   但语义分层，避免一个文件混两类职责。

import { requestInternal, setAccessToken } from './client';
import { ApiError } from './types';

// ───────────────────────────────────────────────────────────────────────────
// 响应类型（镜像 src/backend-express/routes/auth-*.ts 的 200 响应体）
// ───────────────────────────────────────────────────────────────────────────

/** POST /auth/login 与 POST /auth/refresh 的成功响应（auth-login.ts:95 / auth-refresh.ts:78）*/
export interface AuthTokenResp {
  readonly schema: 'auth/v1';
  /** ★ 仅用于 setAccessToken 进内存；**绝不**写 localStorage（§v3-C.3 / §v3-J.1） */
  readonly accessToken: string;
  readonly expiresIn: number;
  readonly tokenType: string;
}

/** POST /auth/register 的成功响应（auth-register.ts:82 / :92）*/
export interface RegisterResp {
  readonly schema: 'auth/v1';
  readonly email: string;
  /** '注册成功，立即可登录' | '用户已存在；可直接 /auth/login' */
  readonly message: string;
}

// ───────────────────────────────────────────────────────────────────────────
// 登录 / 注册
// ───────────────────────────────────────────────────────────────────────────

/**
 * POST /auth/login —— 邮箱 + 密码登录。
 *
 * 成功：服务端已 Set-Cookie（dogtrip_token / dogtrip_refresh），这里把 accessToken
 *       放进**内存**（setAccessToken），供后续 GET /auth/me / GET /save 带 Authorization 头。
 * 失败：401（邮箱或密码不对）→ throw ApiError(401)；网络不通 → throw ApiError(0)。
 *       文案映射在 src/views/AuthPanel.tsx（UX §6 auth-15 / auth-18）。
 */
export async function login(email: string, password: string): Promise<AuthTokenResp> {
  const resp = await requestInternal<AuthTokenResp>('POST', '/auth/login', {
    auth: false, // 登录时还没有 token
    body: { email, password },
  });
  setAccessToken(resp.accessToken);
  return resp;
}

/**
 * POST /auth/register —— 邮箱 + 密码注册（服务端自动 AdminConfirmSignUp，注册完立即可登录）。
 *
 * ★ 本方法**不**自动登录：调用方（AuthPanel）决定是否紧接着 login()，
 *   以便先显示 UX §3.6 的 auth-13「好了。以后换设备，它去过的地方还在。」。
 *
 * 幂等：邮箱已存在 → 服务端返回 200 + message='用户已存在；可直接 /auth/login'（不报错）。
 */
export async function register(email: string, password: string): Promise<RegisterResp> {
  return requestInternal<RegisterResp>('POST', '/auth/register', {
    auth: false,
    body: { email, password },
  });
}

// ───────────────────────────────────────────────────────────────────────────
// refresh（§v3-C.5：bootstrap 启动时调，用 cookie 换内存 access token）
// ───────────────────────────────────────────────────────────────────────────

/**
 * POST /auth/refresh —— 用 httpOnly refresh cookie 换新的 access token。
 *
 * ★ 请求体**不带** refresh token（它只在 cookie 里，JS 读不到 —— §v3-C.3 安全基线）。
 * ★ 本端点**不挂** JWT 中间件（refresh 时 access token 通常已过期）。
 *
 * 调用时机（§v3-C.5 bootstrap）：
 *   - 应用启动：有 cookie 就能静默续期，玩家不必每次重新输密码
 *   - 收到 401：续期成功则重试原请求（S7 才接进统一 401 兜底，本工单只提供能力）
 *
 * 无 cookie / cookie 过期 → throw ApiError(401)；调用方**必须**容忍这个失败
 * （首次访问的玩家本来就没有 cookie，401 是正常路径，不是异常）。
 */
export async function refresh(): Promise<AuthTokenResp> {
  const resp = await requestInternal<AuthTokenResp>('POST', '/auth/refresh', {
    auth: false,
    body: {}, // 空 body 只为触发 Content-Type: application/json；token 全走 cookie
  });
  setAccessToken(resp.accessToken);
  return resp;
}

// ───────────────────────────────────────────────────────────────────────────
// 登出
// ───────────────────────────────────────────────────────────────────────────

/**
 * POST /auth/logout —— 清服务端 cookie + 清内存 token。
 *
 * §v3-G 风险 #6（令牌串号缓解）：登出后 access token 立即从内存丢弃；
 * 本地 `dogtrip.save` 的清理由设置面板的登出确认流程负责（UX §3.4 两个必处理坑），
 * 本文件**不**碰 localStorage（§v3-J.1 第 2 条 —— 那不是认证层的职责）。
 *
 * 失败也**不抛**：登出的语义是"本地立即失效"，服务端失败只记 console，
 * 玩家已经回到登录框（AuthGuard 保证），不会因为 500 卡在半登出态。
 */
export async function logout(): Promise<void> {
  try {
    await requestInternal<{ schema: string }>('POST', '/auth/logout', { auth: true, body: {} });
  } catch (err) {
    if (!(err instanceof ApiError)) throw err;
    console.warn('[auth] logout 服务端失败（本地已失效）：', err.message);
  }
  setAccessToken(null);
}
